import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import ProductCard from '../components/ProductCard'
import ProductModal from '../components/ProductModal'
import { productsAPI } from '../services/api'
import { Search, Filter, Grid, List, LucideFilterX } from 'lucide-react'

const Products = () => {
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [viewMode, setViewMode] = useState('grid')
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)


  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true)
        setError(null) // Clear any previous errors
        const params = {}

        if (searchTerm) params.search = searchTerm
        if (selectedCategory) params.category = selectedCategory

        const response = await productsAPI.getAll(params)

        // Ensure response has expected structure
        if (response && response.data && response.data.data) {
          setProducts(response.data.data)

          const distinctcategories = ['ALL', ...new Set(response.data.data.map(p => p.category.toLowerCase()))];
          setCategories(
            distinctcategories.map(cat => ({
              value: cat,
              label: cat.charAt(0).toUpperCase() + cat.slice(1) // Title case
            }))
          );
        } else {
          throw new Error('Invalid response format')
        }

      } catch (err) {
        console.error('Error fetching products:', err)
        setError('Failed to load products. Please refresh the page or try again later.')
        setProducts([]) // Clear products on error
        setCategories([{ value: 'ALL', label: 'All' }]) // Reset categories
      } finally {
        setLoading(false)
      }
    }

    fetchProducts()
  }, [searchTerm, selectedCategory])

  const handleSearch = (e) => {
    e.preventDefault()
    setSearchTerm(e.target.value)
  }

  const handleCategoryChange = (category) => {
    if (category === 'All')
      setSelectedCategory('')
    else
      setSelectedCategory(category)
  }

  const handleProductClick = (product) => {
    setSelectedProduct(product)
    setIsModalOpen(true)
  }

  const handleFilterReset = () => {
    setSelectedCategory('');
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setTimeout(() => setSelectedProduct(null), 300) // Clear after animation
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-white py-16">
        <div className="container-custom text-center">
          <h1 className="section-heading mb-4">Our Products</h1>
          <p className="subheading max-w-3xl">
            Premium fertilizers and plant nutrients designed for sustainable agriculture.
            Find the perfect solution for your farming needs.
          </p>
        </div>
      </section>

      {/* Filters and Search */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="container-custom">
          {/* Search and Main Filters */}
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between mb-4">
            {/* Search */}
            <div className="flex-1 w-full lg:max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, description, or NPK..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent shadow-sm"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-3 w-full lg:w-auto">
              <div className="flex items-center gap-2 flex-1 lg:flex-initial">
                <Filter className="h-5 w-5 text-gray-600 flex-shrink-0" />
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="flex-1 lg:w-48 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent shadow-sm bg-white"
                >
                  {categories.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg p-1 shadow-sm">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-all ${viewMode === 'grid'
                      ? 'bg-earth-green text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  title="Grid View"
                >
                  <Grid className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-all ${viewMode === 'list'
                      ? 'bg-earth-green text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  title="List View"
                >
                  <List className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Active Filters and Results Count */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            {/* Active Filters */}
            <div className="flex flex-wrap items-center gap-2">
              {searchTerm && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-earth-green/10 text-earth-green rounded-full text-sm">
                  <span className="font-medium">Search:</span>
                  <span className="max-w-[150px] truncate">{searchTerm}</span>
                  <button
                    onClick={() => setSearchTerm('')}
                    className="hover:bg-earth-green/20 rounded-full p-0.5 transition-colors"
                  >
                    <LucideFilterX className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {selectedCategory && selectedCategory !== 'ALL' && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-blue/10 text-brand-blue rounded-full text-sm">
                  <span className="font-medium">Category:</span>
                  <span className="capitalize">{selectedCategory}</span>
                  <button
                    onClick={handleFilterReset}
                    className="hover:bg-brand-blue/20 rounded-full p-0.5 transition-colors"
                  >
                    <LucideFilterX className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {(searchTerm || (selectedCategory && selectedCategory !== 'ALL')) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('');
                  }}
                  className="text-sm text-gray-600 hover:text-gray-900 underline font-medium"
                >
                  Clear all filters
                </button>
              )}
            </div>

            {/* Results Count */}
            {!loading && (
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{products.length}</span>
                {' '}{products.length === 1 ? 'product' : 'products'} found
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-earth-green"></div>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-600 text-lg">{error}</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">No products found matching your criteria.</p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <p className="text-gray-600">
                  Showing {products.length} product{products.length !== 1 ? 's' : ''}
                  {searchTerm && ` for "${searchTerm}"`}
                  {selectedCategory && ` in ${categories.find(c => c.value === selectedCategory)?.label}`}
                </p>
              </div>

              <div className={`grid gap-8 ${viewMode === 'grid'
                  ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                  : 'grid-cols-1'
                }`}>
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onProductClick={handleProductClick}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Product Modal */}
      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  )
}

export default Products
